// The Manager Maeve conversational surface — the phone-shaped chat the PO
// mocked up (2026-07-31 rework): full-bleed white canvas, content-hugging
// bubbles that alternate sides, Maeve's face beside each of her turns,
// per-turn timestamps inside the bubble, and a pill composer with a teal
// send button.
//
// Rendered by `ManagerAskMaeve` at EVERY width — full-bleed inside the
// bottom-nav app shell below `md`, bounded in a bordered column above it.
// There is no separate desktop layout; see that file for why a JS branch here
// was a data-loss bug rather than a styling choice.
//
// GOVERNANCE — this is presentation only. It rides the same, already-
// CGC-gated `/api/cases/:id/maeve/guidance` endpoint via the shared
// `useManagerGuidanceChat` hook, and adds no new prompt surface. Manager
// Maeve responses are always audit-logged against a specific case's
// `employeeId`, so there is no case-less chat mode: this is a "who's this
// about?" picker in front of the endpoint, then the thread.
//
// ANTI-ANTHROPOMORPHISM (DESIGN §1.4 #8) — this surface carries THREE
// reviewed exceptions to that rule, all from the PO mockup and all approved
// by the CGC (2026-07-31). None is an oversight; do not "fix" them back:
//   1. The **Online** presence dot in the header.
//   2. **Double read-receipt ticks** on the manager's own turn. Rendered only
//      after the server has answered, so the receipt is never shown for a
//      turn still in flight or one that errored.
//   3. The first-person **opening greeting** ("Hi [First Name], how can I
//      help today?"), rendered client-side from the approved mockup copy —
//      not a model call, so it costs no turn and cannot drift.
// The advisory disclaimer deliberately stays a system caption above the
// thread rather than a Maeve bubble, so it can't be mistaken for something
// Maeve said.

import { Link, useSearchParams } from 'react-router';
import type { CaseSummary } from '@besideu/shared/cases';
import type { GuidanceTurn } from '@besideu/shared/maeve';
import { useCases } from '@/lib/use-cases.ts';
import { useManagerGuidanceChat, explainGuidanceError } from '@/lib/use-manager-guidance-chat.ts';
import { useScrollToEnd } from '@/lib/use-scroll-to-end.ts';
import { MaeveAvatar } from '@/components/brand/MaeveAvatar.tsx';
import { RoleChip } from '@/components/Shell.tsx';
import { NavCard } from '@/components/ui/Card.tsx';
import { ChevronLeftIcon, SendIcon } from '@/components/ui/icons.tsx';
import { StatusPill, ToneFor, humaniseStatus } from '@/components/ui/StatusPill.tsx';

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

/** Which case the thread is about. In the URL, not component state — see below. */
const CASE_PARAM = 'case';

export function ManagerMaeveChat({ firstName }: { firstName: string }) {
  // The selected case lives in the QUERY STRING (`/manager/maeve?case=<id>`),
  // deliberately, mirroring the URL-as-state pattern the list surfaces use
  // (`use-list-search-params.ts`). It used to be `useState`, which meant it
  // died with any remount — and `ManagerShell` remounts this whole subtree
  // when the viewport crosses `md`, because the bottom-nav app shell and the
  // desktop chrome are different component trees. Rotating a phone to
  // landscape therefore dumped a mid-conversation manager back on the case
  // picker. (The transcript itself survives independently, in the query
  // cache — see `use-manager-guidance-chat.ts`.) Three things fall out for
  // free: the thread is deep-linkable, the system back gesture returns to the
  // picker instead of leaving the tab, and a reload resumes where you were.
  const [searchParams, setSearchParams] = useSearchParams();
  const caseId = searchParams.get(CASE_PARAM) ?? '';

  const cases = useCases();
  const list = cases.data?.cases ?? [];
  // Resolved from the list rather than trusted from the URL: a stale or
  // hand-edited `?case=` (a closed case, another manager's, a typo) finds
  // nothing and falls through to the picker. `/api/cases` is already
  // self-scoped to the signed-in manager server-side, so this can't be used
  // to address a case they don't own — it just won't render.
  const selected = caseId ? (list.find((c) => c.id === caseId) ?? null) : null;

  function select(c: CaseSummary) {
    // Push, so the system back gesture goes thread → picker.
    setSearchParams({ [CASE_PARAM]: c.id });
  }
  function clearSelection() {
    // Replace, so backing out doesn't leave a thread entry the forward/back
    // buttons bounce between.
    setSearchParams({}, { replace: true });
  }

  // The thread is its own child component mounted only once a case is picked,
  // rather than calling `useManagerGuidanceChat` unconditionally up here.
  //
  // `key={selected.id}` is `useManagerGuidanceChat`'s stated CONTRACT (see its
  // header comment). It matters more now than it did with local state: `?case=`
  // can change from one id straight to another (browser history, a pasted
  // link) with no intervening picker render, and the key is what stops case
  // A's unsent draft appearing under case B.
  //
  // The transcript is NOT discarded by going back: since it moved into the
  // query cache keyed by `caseId`, re-picking the same case in the same
  // session resumes the conversation rather than starting an empty one.
  // Deliberate — the manager didn't ask to throw it away, they asked to look
  // at the list — and case A's turns still cannot reach case B's CGC-audited
  // thread, because the cache key, not the remount, is what separates them.
  if (selected) {
    return (
      <Thread key={selected.id} case={selected} firstName={firstName} onBack={clearSelection} />
    );
  }
  // A `?case=` that hasn't resolved yet is still loading, not invalid — don't
  // flash the picker at someone who deep-linked into a thread.
  return <CasePicker cases={cases} list={list} resolving={caseId !== ''} onSelect={select} />;
}

// -----------------------------------------------------------------------------
// Chrome
// -----------------------------------------------------------------------------

function ChatHeader({
  onBack,
  backDisabled = false,
}: {
  onBack?: () => void;
  /**
   * Disabled while a turn is in flight. This is now a clarity guard, not a
   * correctness one: since the transcript append moved into the mutation's
   * `mutationFn` (see `use-manager-guidance-chat.ts`), a reply lands in the
   * cache even if this component unmounts mid-request. Keeping the button
   * disabled avoids the confusing middle state where the manager backs out,
   * sees the picker, and a turn they can't see is still resolving.
   */
  backDisabled?: boolean;
}) {
  return (
    <header className="flex shrink-0 items-center gap-3 border-b border-border bg-card px-3 py-2.5">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          disabled={backDisabled}
          aria-label="Change support pathway"
          className="-ml-1 rounded-full p-1 text-muted-foreground transition hover:text-foreground disabled:opacity-40"
        >
          <ChevronLeftIcon size={22} />
        </button>
      ) : null}
      <MaeveAvatar size={40} rings={false} />
      <div className="min-w-0 flex-1">
        <p className="font-heading text-[0.95rem] leading-tight font-bold">Maeve</p>
        {/* "Online" presence indicator — PO mockup direction (2026-07-30).
            Reviewed against the DESIGN.md §1.4 #8 anti-anthropomorphism rule
            (which governs "always there" / continuity framing elsewhere in
            the product) and approved by PO/CGC as an intentional exception
            for this surface — not an oversight if it looks inconsistent
            with that rule at a glance. */}
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-primary">
          <span aria-hidden className="size-1.5 rounded-full bg-primary" />
          Online
        </p>
      </div>
      <RoleChip label="Manager" />
    </header>
  );
}

// -----------------------------------------------------------------------------
// Bubbles
// -----------------------------------------------------------------------------

/**
 * NOT the shared `components/chat/ChatBubble.tsx`. That one is the brand
 * §7 document-style bubble used by the desktop cards and the Employee
 * Companion: near-full-width, with a "YOU" / "MAEVE" uppercase header line
 * carrying attribution without relying on colour (WCAG 1.4.1). This surface
 * is a messaging app — bubbles hug their content and alternate sides, so
 * attribution comes from Maeve's face on her turns plus the "You"/"Maeve"
 * text in each bubble's own timestamp row, which keeps the same
 * not-by-colour-alone guarantee at messaging density. Kept local rather
 * than bolted onto the shared component as a third variant so a change
 * here can't reach the Employee Companion's safety-critical transcript.
 */
function Bubble({
  turn,
  at,
  confirmed,
}: {
  turn: GuidanceTurn;
  at: number | undefined;
  /** User turns only: the server has accepted this turn. */
  confirmed: boolean;
}) {
  const isUser = turn.role === 'user';
  const time = at === undefined ? null : TIME_FORMAT.format(new Date(at));

  return (
    // `items-center`, not `items-end`: Maeve's face sits level with the middle
    // of her bubble (per the mockup). Bottom-aligning it strands the face at
    // the very bottom-left of a long multi-paragraph answer, which reads as a
    // rendering slip rather than an attribution mark.
    <div
      className={['flex items-center gap-2', isUser ? 'justify-end' : 'justify-start'].join(' ')}
    >
      {isUser ? null : <MaeveAvatar size={28} rings={false} className="shrink-0 self-center" />}
      <div
        className={[
          'max-w-[78%] px-3.5 py-2.5 text-[0.95rem] whitespace-pre-wrap',
          isUser
            ? 'rounded-2xl rounded-br-md bg-ink text-white'
            : 'rounded-2xl rounded-bl-md bg-teal-light text-navy',
        ].join(' ')}
      >
        <p>{turn.text}</p>
        <p
          className={[
            'mt-1 flex items-center justify-end gap-1 text-[0.65rem]',
            isUser ? 'text-white/55' : 'text-navy/45',
          ].join(' ')}
        >
          {/* Attribution in text, not colour alone (WCAG 1.4.1) — the
              shared ChatBubble does this with an uppercase header line;
              at messaging density it rides the timestamp row instead. */}
          <span className="sr-only">{isUser ? 'You' : 'Maeve'},</span>
          {time ? <span>{time}</span> : null}
          {isUser && confirmed ? <ReadTicks /> : null}
        </p>
      </div>
    </div>
  );
}

// Double read-receipt tick on the manager's own turn, per the PO mockup
// (CGC-approved 2026-07-31). Shown only once the server has answered the
// turn — i.e. Maeve has actually been given the message — so it never claims
// delivery for a turn still in flight or one that errored.
//
// Tinted `text-primary` rather than inheriting the timestamp's `text-white/55`
// so it reads as the mockup's accent ticks against the Ink bubble instead of
// as more muted metadata.
function ReadTicks() {
  return (
    <svg
      width="16"
      height="12"
      viewBox="0 0 22 16"
      fill="none"
      role="img"
      aria-label="Read"
      focusable="false"
      className="text-primary"
    >
      <path
        d="M1.5 8.5 5 12l7.5-8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 8.5 12.5 12 20 4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Maeve's "…" while a turn is in flight. Purely a progress indicator for a
// request/response call (there is no token streaming — the safety pipeline
// must classify the whole turn before any of it is shown; see PROJECT.md
// "Companion — Maeve (M2)"), so it is NOT a "typing" simulation and is
// announced to assistive tech as a status, not as speech.
function PendingBubble() {
  return (
    <div
      className="flex items-end justify-start gap-2"
      role="status"
      aria-label="Maeve is preparing a response"
    >
      <MaeveAvatar size={28} rings={false} className="mb-1 shrink-0" />
      <div className="rounded-2xl rounded-bl-md bg-teal-light px-4 py-3">
        <span className="flex gap-1" aria-hidden>
          {[0, 150, 300].map((delay) => (
            <span
              key={delay}
              className="size-1.5 animate-pulse rounded-full bg-navy/35"
              style={{ animationDelay: `${String(delay)}ms` }}
            />
          ))}
        </span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Picker
// -----------------------------------------------------------------------------

// `cases`/`list` come from the parent, which already runs `useCases()` to
// resolve `?case=` into a `CaseSummary`. Calling the hook again here would be
// harmless (same query key, one fetch) but would split "is this still
// loading?" across two call sites, and `resolving` needs the parent's answer
// anyway.
function CasePicker({
  cases,
  list,
  resolving,
  onSelect,
}: {
  cases: ReturnType<typeof useCases>;
  list: CaseSummary[];
  /** A `?case=` is present but hasn't resolved to a case yet. */
  resolving: boolean;
  onSelect: (c: CaseSummary) => void;
}) {
  return (
    <div className="flex h-full flex-col bg-card">
      <ChatHeader />
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <p className="pb-3 text-sm text-muted-foreground">
          Who&rsquo;s this about? Maeve grounds her answer in that support pathway.
        </p>
        {cases.isPending || (resolving && cases.isFetching) ? (
          <p className="text-muted-foreground">Loading your support pathways…</p>
        ) : cases.error ? (
          <p className="text-traffic-red">Could not load your support pathways.</p>
        ) : list.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-muted-foreground">
              Once you open a support pathway, you can ask Maeve about it here.
            </p>
            <Link
              to="/manager/cases/new"
              className="mt-2 inline-block text-sm font-medium text-primary"
            >
              New support pathway
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {list.map((c) => (
              <li key={c.id}>
                <NavCard
                  onClick={() => onSelect(c)}
                  title={c.employee.name}
                  titleTone="foreground"
                  subtitle={`${c.deceasedRelationship} · loss ${c.dateOfLoss}`}
                  meta={
                    <StatusPill tone={ToneFor(c.status)} dot>
                      {humaniseStatus(c.status)}
                    </StatusPill>
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Thread
// -----------------------------------------------------------------------------

function Thread({
  case: c,
  firstName,
  onBack,
}: {
  case: CaseSummary;
  firstName: string;
  onBack: () => void;
}) {
  const { history, sentAt, ask, draft, setDraft, submit } = useManagerGuidanceChat(
    c.id,
    'ask-maeve',
  );
  const endRef = useScrollToEnd(history.length, ask.isPending);

  return (
    <div className="flex h-full flex-col bg-card">
      <ChatHeader onBack={onBack} backDisabled={ask.isPending} />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {/* Two lines, not one wrapped sentence: the first is the case scope
            (which case Maeve is grounded in — the thing a manager glances up
            to re-check mid-thread), the second the standing advisory
            disclaimer. Run together they wrapped into an undifferentiated
            grey block where neither read as important. */}
        <div className="mb-4 text-center">
          <p className="text-xs text-muted-foreground">
            Asking about <span className="font-medium text-foreground">{c.employee.name}</span>
          </p>
          <p className="mt-0.5 text-[0.7rem] text-muted-foreground/80">
            Advisory — the decision is always yours.
          </p>
        </div>

        <ol className="flex flex-col gap-3" aria-label="Conversation with Maeve">
          {/* Opening turn — PO mockup copy, CGC-approved 2026-07-31.
              Rendered client-side, not a model call, so it costs no turn and
              cannot drift.

              The two variants are NOT interchangeable. The approved mockup
              string is the personalised one; the CGC carve-out is what lets
              it say "how can I help" at all. The JWT `name` claim is empty on
              a pre-Phase-0.2 token or a nameless invite, and the house rule
              (see `ManagerMaeveFirst.tsx`) is that the no-name fallback stays
              inside the §1.4 #8 guardrail rather than being a de-named copy
              of the carve-out — "Hi, how can I help today?" would smuggle a
              prohibited phrase onto a path the approval doesn't cover. This
              reuses the desktop hero's existing neutral copy; the two never
              render on the same route, so there's no duplicate-accessible-
              name collision of the kind that forced `ManagerMaeveFirst` to
              coin its own. */}
          <li>
            <Bubble
              turn={{
                role: 'assistant',
                text: firstName
                  ? `Hi ${firstName}, how can I help today?`
                  : 'Where do you need help today?',
              }}
              at={undefined}
              confirmed={false}
            />
          </li>
          {history.map((turn, idx) => (
            <li key={idx}>
              <Bubble turn={turn} at={sentAt[idx]} confirmed />
            </li>
          ))}
          {ask.isPending ? (
            <li>
              <PendingBubble />
            </li>
          ) : null}
        </ol>

        {ask.error ? (
          <div className="mt-3">
            <ChatError error={ask.error} onRetry={() => ask.reset()} />
          </div>
        ) : null}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="flex shrink-0 items-center gap-2 border-t border-border bg-card px-3 py-2.5"
      >
        <label className="flex-1">
          <span className="sr-only">Message Maeve</span>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Message Maeve…"
            // text-base = 16px to avoid iOS Safari zoom on focus.
            className="w-full rounded-full border border-border bg-background px-4 py-3 text-base placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            maxLength={4000}
            disabled={ask.isPending}
          />
        </label>
        <button
          type="submit"
          disabled={draft.trim().length === 0 || ask.isPending}
          aria-label="Send"
          className="grid size-11 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition disabled:opacity-40"
        >
          <SendIcon size={19} />
        </button>
      </form>
    </div>
  );
}

function ChatError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { headline, detail } = explainGuidanceError(error);
  return (
    <div
      role="status"
      className="space-y-2 rounded-2xl border border-traffic-red/40 bg-traffic-red/5 p-3 text-sm"
    >
      <p className="font-medium text-traffic-red">{headline}</p>
      <p className="text-muted-foreground">{detail}</p>
      <button type="button" onClick={onRetry} className="text-sm font-medium text-primary">
        Dismiss
      </button>
    </div>
  );
}
